'use server';
/**
 * What the wizard offers: the active story templates marked for the wizard and
 * the active illustration templates marked as offered, from their published
 * versions. A template the admin adds appears here without a deploy.
 * Read-only for any signed-in user (no admin role): titles and example URLs.
 */
import { SessionService } from '@/contexts/authentication';
import { getTemplateRepository } from '../repositories/TemplateRepository';
import { getMediaStorage } from '@/contexts/media/storage/StorageProvider';
import type { IllustrationTemplateData, StoryTemplateData } from '../types';
import { isIllustrationStyle } from '@/contexts/illustrations/services/styleTechnique';

export interface OfferedStoryTemplate {
  key: string;
  title: Record<string, string>;
  energy: number | null;
}

export interface OfferedIllustrationTemplate {
  key: string;
  kind: 'book' | 'comic' | 'coloring';
  title: Record<string, string>;
  /** First uploaded example (public URL), or null → the bundled tile. */
  exampleUrl: string | null;
  bundledPreview: string | null;
  minAge: number | null;
}

export interface OfferedTemplates {
  stories: OfferedStoryTemplate[];
  illustrations: OfferedIllustrationTemplate[];
}

export async function offeredTemplates(): Promise<OfferedTemplates | null> {
  // The wizard is signed-in only.
  await SessionService.requireSession();
  try {
    const repo = getTemplateRepository();
    const storage = getMediaStorage();
    const [storyTpls, illTpls] = await Promise.all([repo.list('story'), repo.list('illustration')]);
    const stories: OfferedStoryTemplate[] = [];
    for (const t of storyTpls) {
      if (t.status !== 'active') continue;
      const v = await repo.published<StoryTemplateData>(t.id);
      if (!v || !v.data.offeredInWizard) continue;
      stories.push({ key: t.key, title: v.data.title, energy: v.data.defaults.energy ?? null });
    }
    const illustrations: OfferedIllustrationTemplate[] = [];
    for (const t of illTpls) {
      if (t.status !== 'active') continue;
      // Pages are still drawn by the style code; a brand-new illustration
      // template is offered once pipeline v2 draws from templates.
      if (!isIllustrationStyle(t.key)) continue;
      const v = await repo.published<IllustrationTemplateData>(t.id);
      if (!v || !v.data.offered) continue;
      const first = v.data.examples[0]?.key;
      illustrations.push({
        key: t.key,
        kind: v.data.kind,
        title: v.data.title,
        exampleUrl: first ? await storage.url(first, { visibility: 'public' }) : null,
        bundledPreview: v.data.bundledPreview ?? null,
        minAge: v.data.recommendedAge.min ?? null,
      });
    }
    return { stories, illustrations };
  } catch {
    // Table not there yet → the wizard keeps its built-in lists.
    return null;
  }
}
